/* eslint-disable no-console */
/**
 * Console output for the analytics layer. Everything here is gated on
 * `config.debug`, and every payload is masked before it is printed —
 * full mobile numbers, emails, names or DOBs never reach the console.
 */
import { config } from './config';

export const PII_KEYS = Object.freeze(['mobile_number', 'email_id', 'dob', 'full_name', 'mobile', 'name']);
const MASKED_KEYS = Object.freeze([...PII_KEYS, 'client_name', 'user_name']);

/** 9876543210 → 98******10 */
export const maskMobile = (value) => {
    const s = String(value);
    if (s.length <= 4) return '*'.repeat(s.length);
    return `${s.slice(0, 2)}${'*'.repeat(s.length - 4)}${s.slice(-2)}`;
};

/** abcdef@gmail.com → ab***@gmail.com */
export const maskEmail = (value) => {
    const s = String(value);
    const at = s.indexOf('@');
    if (at < 0) return maskMobile(s);
    return `${s.slice(0, Math.min(2, at))}***${s.slice(at)}`;
};

/** 1990-05-17 → ****-**-17 ; names → R*** */
const maskOther = (key, value) => {
    const s = String(value);
    if (key === 'dob') return s.replace(/\d(?=.{2})/g, '*');
    return s ? `${s[0]}***` : s;
};

export const maskValue = (key, value) => {
    if (value == null || value === '') return value;
    if (key === 'mobile_number' || key === 'mobile') return maskMobile(value);
    if (key === 'email_id') return maskEmail(value);
    return maskOther(key, value);
};

/** Shallow copy of `props` with PII masked, for console output only. */
export const maskProps = (props) => {
    const out = {};
    Object.keys(props || {}).forEach((key) => {
        out[key] = MASKED_KEYS.includes(key) ? maskValue(key, props[key]) : props[key];
    });
    return out;
};

const QA_BUFFER_SIZE = 100;

/**
 * QA helper: keep the last 100 tracked events (masked) on `window.__moeEvents`.
 * Local dev / test builds only, and only with debug on. Never in a production
 * build (`craco build`, which every deployed env uses), even with
 * REACT_APP_MOE_DEBUG=true — CRA inlines NODE_ENV, so this code is dropped there.
 * @param {string} name
 * @param {Record<string, unknown>} payload  final payload (after PII stripping)
 * @param {'mock'|'live'} mode
 * @param {string} [legacyName]  EVENTS.* name the call site used, when MoEngage gets a different one
 */
export const recordForQa = (name, payload, mode, legacyName) => {
    try {
        if (process.env.NODE_ENV === 'production') return;
        if (!config.enabled || !config.debug || typeof window === 'undefined') return;
        if (!Array.isArray(window.__moeEvents)) window.__moeEvents = [];
        window.__moeEvents.push({
            name, legacyName: legacyName || name, mode, at: new Date().toISOString(), props: maskProps(payload),
        });
        if (window.__moeEvents.length > QA_BUFFER_SIZE) {
            window.__moeEvents.splice(0, window.__moeEvents.length - QA_BUFFER_SIZE);
        }
    } catch (_) { /* QA aid only */ }
};

export const debugInfo = (...args) => {
    if (config.enabled && config.debug) console.info(...args);
};

export const debugWarn = (...args) => {
    if (config.enabled && config.debug) console.warn(...args);
};
